/**
 * TUVI - Modal para registrar/editar un ajuste de cartera:
 * Reintegro a cliente (devolución de dinero) o Descuento por incumplimiento.
 * Ambos reducen el valor total del proyecto (modelo A).
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import './AjustesCartera.css';

const norm = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
const hoyCO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

const AjusteCarteraModal = ({ show, onClose, onSaved, proyectoFijo = null, editando = null }) => {
  const { success, error } = useNotification();
  const fileRef = useRef(null);
  const [guardando, setGuardando] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [busquedaProy, setBusquedaProy] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [archivo, setArchivo] = useState(null);          // File nuevo
  const [archivoKey, setArchivoKey] = useState(null);     // key ya guardada (edición)
  const [archivoNombre, setArchivoNombre] = useState('');
  const [form, setForm] = useState({ tipo: 'reintegro', proyecto_id: null, valor: '', fecha: hoyCO(), motivo: '' });

  useEffect(() => {
    if (!show) return;
    // Inicializar según edición / proyecto fijo
    if (editando) {
      setForm({
        tipo: editando.tipo || 'reintegro',
        proyecto_id: editando.proyecto_id,
        valor: editando.valor || '',
        fecha: editando.fecha || hoyCO(),
        motivo: editando.motivo || '',
      });
      setArchivoKey(editando.comprobante_archivo || null);
      setArchivoNombre(editando.comprobante_nombre || '');
      setBusquedaProy('');
    } else {
      setForm({ tipo: 'reintegro', proyecto_id: proyectoFijo?.id || null, valor: '', fecha: hoyCO(), motivo: '' });
      setArchivoKey(null); setArchivoNombre(''); setBusquedaProy('');
    }
    setArchivo(null);
    // Cargar proyectos si hay que buscar
    if (!proyectoFijo) {
      client.query(`{ proyectos(incluir_especiales: true, incluir_posventa: true) { id proyecto_nombre nombre_cliente apartamento } }`)
        .then(d => setProyectos(d.proyectos || [])).catch(() => {});
    }
  }, [show, editando, proyectoFijo]);

  const resultados = useMemo(() => {
    if (!busquedaProy.trim()) return [];
    const palabras = norm(busquedaProy).split(' ').filter(Boolean);
    return proyectos.filter(p => {
      const t = norm([p.proyecto_nombre, p.apartamento, p.nombre_cliente].filter(Boolean).join(' '));
      return palabras.every(w => t.includes(w));
    }).slice(0, 12);
  }, [busquedaProy, proyectos]);

  const proySeleccionado = proyectoFijo
    || proyectos.find(p => p.id === form.proyecto_id)
    || (editando && form.proyecto_id ? { id: form.proyecto_id, proyecto_nombre: editando.proyecto_nombre, apartamento: editando.apartamento, nombre_cliente: editando.cliente_nombre } : null);

  const setFile = (file) => {
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!okTypes.includes(file.type)) { error('Solo imágenes o PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { error('Archivo muy grande (máx 10MB)'); return; }
    setArchivo(file); setArchivoNombre(file.name); setArchivoKey(null);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.type.startsWith('image/')) {
        const blob = it.getAsFile();
        if (blob) { const ext = blob.type.split('/')[1] || 'png'; setFile(new File([blob], `comprobante_${Date.now()}.${ext}`, { type: blob.type })); }
        e.preventDefault(); return;
      }
    }
  };

  const guardar = async () => {
    if (!form.proyecto_id) { error('Selecciona un proyecto'); return; }
    const valor = parseFloat(form.valor) || 0;
    if (valor <= 0) { error('El valor debe ser mayor a 0'); return; }
    if (!form.motivo.trim()) { error('El motivo es obligatorio'); return; }
    if (form.tipo === 'reintegro' && !archivo && !archivoKey) { error('El reintegro requiere adjuntar el comprobante'); return; }
    try {
      setGuardando(true);
      let key = archivoKey, nombre = archivoNombre;
      if (archivo) {
        const token = localStorage.getItem('tuvi_token');
        const fd = new FormData();
        fd.append('files', archivo);
        const up = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=cartera/ajustes/&bucket=comprobantes`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
        const upData = await up.json();
        if (upData.files?.[0]?.key) { key = upData.files[0].key; nombre = archivo.name; }
        else { error('No se pudo subir el comprobante'); setGuardando(false); return; }
      }
      const input = {
        proyecto_id: parseInt(form.proyecto_id), tipo: form.tipo, valor,
        fecha: form.fecha, motivo: form.motivo.trim(),
        comprobante_archivo: key || null, comprobante_nombre: nombre || null,
      };
      let r;
      if (editando) {
        r = await client.mutate(`mutation($id: Int!, $input: CarteraAjusteInput!) { editarCarteraAjuste(id: $id, input: $input) { success message } }`, { id: editando.id, input });
        r = r.editarCarteraAjuste;
      } else {
        r = await client.mutate(`mutation($input: CarteraAjusteInput!) { crearCarteraAjuste(input: $input) { success message } }`, { input });
        r = r.crearCarteraAjuste;
      }
      if (r.success) { success(r.message); onSaved && onSaved(); onClose(); }
      else error(r.message || 'No se pudo guardar');
    } catch (err) { error('Error al guardar el ajuste'); }
    finally { setGuardando(false); }
  };

  if (!show) return null;

  return (
    <div className="ajc-modal-overlay" onPaste={handlePaste} onClick={onClose}>
      <div className="ajc-modal" onClick={e => e.stopPropagation()}>
        <div className="ajc-modal-header">
          <h3>{editando ? 'Editar ajuste' : 'Nuevo reintegro / descuento'}</h3>
          <button className="ajc-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="ajc-modal-body">
          {/* Tipo */}
          <div className="ajc-field">
            <label>Tipo *</label>
            <div className="ajc-tipo-toggle">
              <button type="button" className={`ajc-tipo-btn ${form.tipo === 'reintegro' ? 'act-reint' : ''}`} onClick={() => setForm({ ...form, tipo: 'reintegro' })}>↩️ Reintegro a cliente</button>
              <button type="button" className={`ajc-tipo-btn ${form.tipo === 'descuento' ? 'act-desc' : ''}`} onClick={() => setForm({ ...form, tipo: 'descuento' })}>🏷️ Descuento por incumplimiento</button>
            </div>
            <small className="ajc-hint">
              {form.tipo === 'reintegro'
                ? 'Devuelves dinero al cliente. Baja el valor total y el recaudado neto.'
                : 'Rebaja acordada con el cliente. Baja el valor total y lo que el cliente debe.'}
            </small>
          </div>

          {/* Proyecto */}
          <div className="ajc-field">
            <label>Proyecto *</label>
            {proyectoFijo ? (
              <div className="ajc-proy-fijo"><strong>{proyectoFijo.proyecto_nombre}</strong>{proyectoFijo.apartamento ? ` · Apto ${proyectoFijo.apartamento}` : ''}{proyectoFijo.nombre_cliente ? ` — ${proyectoFijo.nombre_cliente}` : ''}</div>
            ) : proySeleccionado && !editando ? (
              <div className="ajc-proy-sel">
                <span><strong>{proySeleccionado.proyecto_nombre}</strong>{proySeleccionado.apartamento ? ` · Apto ${proySeleccionado.apartamento}` : ''}{proySeleccionado.nombre_cliente ? ` — ${proySeleccionado.nombre_cliente}` : ''}</span>
                <button type="button" className="ajc-btn-mini" onClick={() => { setForm({ ...form, proyecto_id: null }); setBusquedaProy(''); }}>Cambiar</button>
              </div>
            ) : editando ? (
              <div className="ajc-proy-fijo"><strong>{editando.proyecto_nombre}</strong>{editando.apartamento ? ` · Apto ${editando.apartamento}` : ''}</div>
            ) : (
              <div className="ajc-proy-search">
                <input type="text" value={busquedaProy} onChange={e => { setBusquedaProy(e.target.value); setShowResults(true); }} placeholder="Buscar por nombre de proyecto o apartamento..." />
                {showResults && resultados.length > 0 && (
                  <div className="ajc-search-results">
                    {resultados.map(p => (
                      <div key={p.id} className="ajc-search-item" onClick={() => { setForm({ ...form, proyecto_id: p.id }); setShowResults(false); }}>
                        <strong>{p.proyecto_nombre}</strong>{p.apartamento ? ` · Apto ${p.apartamento}` : ''} <span className="ajc-muted">({p.nombre_cliente})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Valor + Fecha */}
          <div className="ajc-row">
            <div className="ajc-field">
              <label>Valor *</label>
              <input type="number" min="0" step="any" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} placeholder="0" />
            </div>
            <div className="ajc-field">
              <label>Fecha *</label>
              <input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
            </div>
          </div>

          {/* Motivo */}
          <div className="ajc-field">
            <label>Motivo *</label>
            <textarea rows="2" value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })} placeholder={form.tipo === 'reintegro' ? '¿Por qué se le devuelve dinero al cliente?' : '¿Por qué se acordó el descuento?'} />
          </div>

          {/* Comprobante */}
          <div className="ajc-field">
            <label>Comprobante {form.tipo === 'reintegro' ? '*' : '(opcional)'}</label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
            {(archivo || archivoKey) ? (
              <div className="ajc-file">
                <span>📎 {archivoNombre || 'comprobante'}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {archivoKey && !archivo && <a className="ajc-btn-mini" href={`${UPLOAD_URL}/catalogo/img/comprobantes/${archivoKey}`} target="_blank" rel="noopener noreferrer">Ver</a>}
                  <button type="button" className="ajc-btn-mini danger" onClick={() => { setArchivo(null); setArchivoKey(null); setArchivoNombre(''); }}>Quitar</button>
                </div>
              </div>
            ) : (
              <button type="button" className="ajc-btn-upload" onClick={() => fileRef.current?.click()}>Subir archivo · o pega con Ctrl+V</button>
            )}
          </div>
        </div>

        <div className="ajc-modal-footer">
          <button className="ajc-btn-cancel" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button className="ajc-btn-save" onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : (editando ? 'Guardar cambios' : 'Registrar')}</button>
        </div>
      </div>
    </div>
  );
};

export default AjusteCarteraModal;
